<template>
  <div class="edit edit__gym">
    <validation-observer ref="observer">
      <v-container
        fluid
        class="px-0"
        v-for="(row, index) in fieldsRow"
        :key="index"
      >
        <div
          class="text-h5 mb-3"
          v-if="row.title"
        >{{row.title}}</div>

        <v-row>
          <v-col
            v-for="(field, cIndex) in row.fields"
            :key="cIndex"
            v-bind="field.colProps || row.colsProp"
            class="py-0"
          >
            <validation-provider
              v-slot="{ errors }"
              :name="field.id"
              :rules="field.rules"
            >
              <component
                :is="field.component"
                v-model="localValue[field.id]"
                :label="field.label"
                :error-messages="getErrorMessage(errors, fieldErrors[field.id])"
                v-bind="field.props"
                outlined
              />
            </validation-provider>
          </v-col>
        </v-row>

        <v-divider
          class="mb-3 mt-2"
          v-if="index + 1 !== fieldsRow.length"
        />
      </v-container>
      <v-container v-if="entityData.id" fluid class="px-0">
        <v-divider class="mb-4" />
        <div class="text-h5 mb-3">Техническое обслуживание</div>
        <p>Все даты и время указаны в вашем локальном часовом поясе.</p>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="maintenanceStartInput"
              label="Начало ТО"
              type="datetime-local"
              step="1"
              outlined
              :error-messages="fieldErrors.maintenance_start"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="maintenanceEndInput"
              label="Окончание ТО"
              type="datetime-local"
              step="1"
              outlined
              :error-messages="fieldErrors.maintenance_end"
            />
          </v-col>
        </v-row>
        <p role="status">Статус: {{ maintenanceState }}</p>
        <p class="text-body-2">После завершения даты сохраняются. Для следующего ТО замените период.</p>
        <v-btn outlined @click="clearMaintenance">
          Очистить период
        </v-btn>
        <v-alert v-if="maintenanceError" type="error" class="mt-4" role="alert">
          {{ maintenanceError }}
        </v-alert>
      </v-container>
    </validation-observer>
  </div>
</template>

<script>
import { Entity, Validation } from '@/mixins';
import { VTextField, VTextarea, VCheckbox } from 'vuetify/lib';
import { FileInputSelect, NumberInput } from '@/components/UI';
import { formatGymDate, parseGymDate, maintenanceStatus } from '@/utils/gymMaintenance';

export default {
  name: 'edit-gym',

  mixins: [Entity, Validation],

  components: {
    VTextField,
    VTextarea,
    VCheckbox,
    FileInputSelect,
    NumberInput
  },

  data: () => ({
    maintenanceStartInput: '',
    maintenanceEndInput: '',
    maintenanceError: '',
    maintenanceNow: Math.floor(Date.now() / 1000),
    maintenanceTimer: null
  }),

  mounted() {
    this.loadMaintenance();
    this.maintenanceTimer = setInterval(() => {
      this.maintenanceNow = Math.floor(Date.now() / 1000);
    }, 1000);
  },

  beforeDestroy() {
    clearInterval(this.maintenanceTimer);
  },

  watch: {
    entityData: 'loadMaintenance',
    maintenanceStartInput: 'maintenanceChanged',
    maintenanceEndInput: 'maintenanceChanged'
  },

  computed: {
    maintenancePeriod() {
      try {
        return {
          start: parseGymDate(this.maintenanceStartInput),
          end: parseGymDate(this.maintenanceEndInput)
        };
      } catch (_) {
        return { start: NaN, end: NaN };
      }
    },

    maintenanceState() {
      return maintenanceStatus(this.maintenancePeriod.start, this.maintenancePeriod.end, this.maintenanceNow);
    },

    maintenancePeriodError() {
      const { start, end } = this.maintenancePeriod;
      if (start === null && end === null) return '';
      if (start === null || end === null) return 'Заполните обе даты ТО или очистите период.';
      if (!Number.isFinite(start) || !Number.isFinite(end)) return 'Укажите корректные дату и время.';
      if (end <= start) return 'Окончание ТО должно быть позже начала.';
      return '';
    },

    fieldsRow() {
      return [
        {
          // Свойства для всех колонок в строке
          colsProp: {
            cols: 12,
            sm: 6,
            lg: 3
          },

          fields: [
            {
              id: 'active',
              label: 'Активен',
              component: 'VCheckbox',
              props: { class: 'mt-0' }
            },
            {
              id: 'has_shower',
              label: 'Есть душ',
              component: 'VCheckbox',
              props: { class: 'mt-0' }
            },
            {
              id: 'around_the_clock',
              label: 'Круглосуточно',
              component: 'VCheckbox',
              props: { class: 'mt-0' }
            }
          ]
        },

        {
          // Свойства для всех колонок в строке
          colsProp: {
            cols: 12
          },

          fields: [
            {
              id: 'title',
              label: 'Название',
              component: 'VTextField',
              rules: 'required',
              colProps: {
                cols: 12,
                lg: 6
              }
            },
            {
              id: 'price',
              label: 'Базовая цена',
              component: 'NumberInput',
              rules: 'required',
              colProps: {
                cols: 12,
                lg: 6
              },
              props: {
                max: 99999999,
                min: 0
              }
            },
            {
              id: 'address',
              label: 'Адрес',
              component: 'VTextField',
              rules: 'required',
              colProps: {
                cols: 12
              }
            },
            {
              id: 'how_to_find',
              label: 'Как найти',
              component: 'VTextarea',
              rules: 'required',
              colProps: {
                cols: 12
              }
            },
            {
              id: 'door_key',
              label: 'Ключ API для двери',
              component: 'VTextField',
              rules: '',
              colProps: {
                cols: 12,
                lg: 6
              },
            },
            {
              id: 'door_ip',
              label: 'IP адрес для двери',
              component: 'VTextField',
              rules: 'ip',
              colProps: {
                cols: 12,
                lg: 6
              },
            },
            {
              id: 'free_cancel_time',
              label:
                'Количество секунд за которое можно отменить бронь бесплатно',
              component: 'NumberInput',
              rules: 'required',
              colProps: {
                cols: 12,
                lg: 6
              },
              props: {
                max: 99999999,
                min: 0
              }
            },
            {
              id: 'min_cancel_time',
              label:
                'Минимальное количество времени за которое заплатит клиент если отменяет бронь платно',
              component: 'NumberInput',
              rules: 'required',
              colProps: {
                cols: 12,
                lg: 6
              },
              props: {
                max: 99999999,
                min: 0
              }
            }
          ]
        },

        {
          fields: [
            {
              id: 'files',
              label: 'Картинки',
              component: 'FileInputSelect',
              colProps: {
                cols: 12,
                lg: 6
              },
              props: {
                multiple: true
              }
            },
            {
              id: 'howToFindImages',
              label: 'Как найти (картинки)',
              component: 'FileInputSelect',
              colProps: {
                cols: 12,
                lg: 6
              },
              props: {
                multiple: true
              }
            }
          ]
        }
      ];
    }
  },

  methods: {
    loadMaintenance() {
      try {
        this.maintenanceStartInput = formatGymDate(this.entityData.maintenance_start);
        this.maintenanceEndInput = formatGymDate(this.entityData.maintenance_end);
      } catch (_) {
        this.maintenanceError = 'Не удалось прочитать период ТО.';
      }
    },

    clearMaintenance() {
      this.maintenanceStartInput = '';
      this.maintenanceEndInput = '';
    },

    maintenanceChanged() {
      this.maintenanceError = this.maintenancePeriodError;
      if (!this.maintenanceError) {
        const { start, end } = this.maintenancePeriod;
        this.$set(this.localValue, 'maintenance_start', start);
        this.$set(this.localValue, 'maintenance_end', end);
      }
    },

    async validate(item) {
      const valid = await Validation.methods.validate.call(this, item);
      if (!this.entityData.id) return valid;
      this.maintenanceError = this.maintenancePeriodError;
      const maintenanceValid = !this.maintenanceError;
      if (maintenanceValid) {
        this.$set(this.localValue, 'maintenance_start', this.maintenancePeriod.start);
        this.$set(this.localValue, 'maintenance_end', this.maintenancePeriod.end);
      }
      return valid && maintenanceValid;
    }
  }
};
</script>
